import { useState, useRef, useEffect } from 'react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Loader2, MapPin } from 'lucide-react';
import { useAddressSearch, GeocodeCandidate } from '../hooks/useAddressSearch';
import { cn } from '@/lib/utils';

interface AddressAutocompleteProps {
  value: string;
  city?: string;
  region?: string;
  onChange: (value: string) => void;
  onSelect: (candidate: GeocodeCandidate) => void;
  placeholder?: string;
  id?: string;
}

export function AddressAutocomplete({
  value,
  city,
  region,
  onChange,
  onSelect,
  placeholder = 'T.ex. Gårdsvägen 12',
  id = 'address',
}: AddressAutocompleteProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const { search, clearResults, isSearching, results, error } = useAddressSearch({
    debounceMs: 400,
    minLength: 4,
  });

  // Handle input change
  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    onChange(newValue);
    
    // Trigger search
    search({ address: newValue, city, region });
    setIsOpen(true);
    setHighlightedIndex(-1);
  };

  // Handle candidate selection
  const handleSelect = (candidate: GeocodeCandidate) => {
    onSelect(candidate);
    clearResults();
    setIsOpen(false);
    setHighlightedIndex(-1);
  };

  // Handle keyboard navigation
  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (!isOpen || results.length === 0) return;

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setHighlightedIndex((prev) =>
          prev < results.length - 1 ? prev + 1 : prev
        );
        break;
      case 'ArrowUp':
        e.preventDefault();
        setHighlightedIndex((prev) => (prev > 0 ? prev - 1 : -1));
        break;
      case 'Enter':
        e.preventDefault();
        if (highlightedIndex >= 0 && highlightedIndex < results.length) {
          handleSelect(results[highlightedIndex]);
        }
        break;
      case 'Escape':
        setIsOpen(false);
        setHighlightedIndex(-1);
        break;
    }
  };

  // Handle click outside to close
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Show dropdown when results arrive
  useEffect(() => {
    if (results.length > 0) {
      setIsOpen(true);
    }
  }, [results]);

  const showDropdown = isOpen && (results.length > 0 || error);

  return (
    <div ref={containerRef} className="relative">
      <Label htmlFor={id}>Adress</Label>
      <div className="relative">
        <Input
          ref={inputRef}
          id={id}
          value={value}
          onChange={handleChange}
          onKeyDown={handleKeyDown}
          onFocus={() => {
            if (results.length > 0) setIsOpen(true);
          }}
          placeholder={placeholder}
          autoComplete="off"
          className="pr-8"
        />
        {isSearching && (
          <div className="absolute right-2 top-1/2 -translate-y-1/2">
            <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
          </div>
        )}
      </div>

      {showDropdown && (
        <div className="absolute z-50 w-full mt-1 bg-popover border border-border rounded-md shadow-lg max-h-[300px] overflow-y-auto">
          {error ? (
            <div className="px-3 py-2 text-sm text-destructive">{error}</div>
          ) : results.length === 0 ? (
            <div className="px-3 py-2 text-sm text-muted-foreground">
              Ingen träff – prova mer specifik adress
            </div>
          ) : (
            <ul role="listbox" className="py-1">
              {results.map((candidate, index) => (
                <li
                  key={`${candidate.lat}-${candidate.lng}-${index}`}
                  role="option"
                  aria-selected={index === highlightedIndex}
                  className={cn(
                    'px-3 py-2 cursor-pointer hover:bg-accent transition-colors',
                    index === highlightedIndex && 'bg-accent'
                  )}
                  onClick={() => handleSelect(candidate)}
                  onMouseEnter={() => setHighlightedIndex(index)}
                >
                  <div className="flex items-start gap-2">
                    <MapPin className="h-4 w-4 mt-0.5 shrink-0 text-muted-foreground" />
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium text-foreground truncate">
                        {formatMainLine(candidate)}
                      </div>
                      <div className="text-xs text-muted-foreground truncate">
                        {formatSecondaryLine(candidate)}
                      </div>
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}

// Format the main line: street and city
function formatMainLine(candidate: GeocodeCandidate): string {
  const parts: string[] = [];
  if (candidate.street) parts.push(candidate.street);
  if (candidate.city) parts.push(candidate.city);
  return parts.length > 0 ? parts.join(', ') : candidate.label.split(',')[0] || '';
}

// Format secondary line: postcode, county, country
function formatSecondaryLine(candidate: GeocodeCandidate): string {
  const parts: string[] = [];
  if (candidate.postcode) parts.push(candidate.postcode);
  if (candidate.county) parts.push(candidate.county);
  
  // If we have no useful parts, use the rest of the label
  if (parts.length === 0) {
    const labelParts = candidate.label.split(',').slice(1);
    return labelParts.join(',').trim();
  }
  
  return parts.join(', ');
}
